<script setup lang="ts">
  import { computed, useSlots } from 'vue';
  import { ButtonAppearance, ButtonType, ButtonSize } from './Button.types';
  import { TypoVariant, Typo, type IconName, IconSize, Icon, Spinner, SpinnerSize, SpinnerColor } from '@/components';

  export interface ButtonProps {
    appearance?: `${ButtonAppearance}`;
    type?: `${ButtonType}`;
    size?: `${ButtonSize}`;
    disabled?: boolean;
    clickable?: boolean;
    loading?: boolean;
    icon?: `${IconName}`;
    iconLeft?: `${IconName}`;
    iconRight?: `${IconName}`;
  }

  const props = withDefaults(defineProps<ButtonProps>(), {
    type: ButtonType.Button,
    size: ButtonSize.M,
    appearance: ButtonAppearance.Primary,
    disabled: false,
    clickable: true,
    loading: false,
    icon: undefined,
    iconLeft: undefined,
    iconRight: undefined,
  });

  const ICON_SIZES = {
    [ButtonSize.XL]: IconSize.XL,
    [ButtonSize.L]: IconSize.L,
    [ButtonSize.M]: IconSize.M,
    [ButtonSize.S]: IconSize.S,
  };

  const TEXT_VARIANTS = {
    [ButtonType.Button]: {
      [ButtonSize.S]: TypoVariant.BodyMRegular,
      [ButtonSize.M]: TypoVariant.BodyLMedium,
      [ButtonSize.XL]: TypoVariant.BodyXLMediumShort,
    },
    [ButtonType.Text]: {
      [ButtonSize.S]: TypoVariant.BodyMRegular,
    },
    [ButtonType.Multiline]: {
      [ButtonSize.L]: TypoVariant.Heading2,
      [ButtonSize.M]: TypoVariant.Heading5,
      [ButtonSize.S]: TypoVariant.BodyLBold,
    },
    [ButtonType.DashLink]: {
      [ButtonSize.L]: TypoVariant.BodyLRegular,
      [ButtonSize.M]: TypoVariant.BodyMRegular,
      [ButtonSize.S]: TypoVariant.BodyXSRegular,
    },
    [ButtonType.Link]: {
      [ButtonSize.S]: TypoVariant.BodyMRegular,
    },
  };

  const DEFAULT_TEXT_VARIANTS = {
    [ButtonSize.XL]: TypoVariant.BodyXLMedium,
    [ButtonSize.L]: TypoVariant.BodyLMedium,
    [ButtonSize.M]: TypoVariant.BodyLRegular,
    [ButtonSize.S]: TypoVariant.BodyLRegular,
  };

  const SUBTEXT_VARIANTS = {
    [ButtonSize.L]: TypoVariant.BodyXLRegular,
    [ButtonSize.M]: TypoVariant.BodyLRegular,
    [ButtonSize.S]: TypoVariant.BodyXSRegular,
  };

  const SPINNER_SIZES = {
    [ButtonSize.XL]: SpinnerSize.M,
    [ButtonSize.L]: SpinnerSize.M,
    [ButtonSize.M]: SpinnerSize.M,
    [ButtonSize.S]: SpinnerSize.S,
  };

  const iconSize = computed(() => {
    return ICON_SIZES[props.size] || IconSize.M;
  });

  const textVariant = computed(() => {
    return TEXT_VARIANTS[props.type]?.[props.size] || DEFAULT_TEXT_VARIANTS[props.size];
  });

  const subtextVariant = computed(() => {
    return SUBTEXT_VARIANTS[props.size] || TypoVariant.BodyMRegular;
  });

  const spinnerSize = computed(() => {
    return SPINNER_SIZES[props.size] || SpinnerSize.S;
  });

  const spinnerColor = computed(() => {
    return props.appearance === ButtonAppearance.Secondary || props.appearance === ButtonAppearance.Tertiary
      ? SpinnerColor.Primary
      : SpinnerColor.White;
  });

  const slots = useSlots();

  const isLeftIconVisible = computed(() => {
    if (props.type === ButtonType.Icon) return !props.loading;
    if (props.iconLeft) return !!slots.default?.() || !props.loading;
    return false;
  });
</script>

<template>
  <button
    type="button"
    :aria-label="icon"
    :disabled="disabled || loading || !clickable"
    :class="[
      'tl-button',
      `tl-button_size-${size}`,
      `tl-button_type-${type}`,
      `tl-button_appearance-${appearance}`,
      { 'tl-button_disabled': disabled },
      { 'tl-button_clickable': clickable },
      { 'tl-button_loading': loading },
    ]"
    v-on="$listeners"
  >
    <Icon
      v-if="isLeftIconVisible"
      :name="iconLeft || icon"
      :size="iconSize"
      data-test-id="icon-left"
      class="tl-button__icon"
    />

    <div
      v-if="type !== ButtonType.Icon && ($slots.default || $slots.subtext)"
      :class="['tl-button__text', { 'tl-button__text_hidden': loading }]"
    >
      <Typo v-if="type === ButtonType.Multiline" data-test-id="sub-text" :variant="subtextVariant">
        <slot name="subtext" />
      </Typo>

      <Typo data-test-id="main-text" class="tl-button__main-text" :variant="textVariant">
        <slot />
      </Typo>
    </div>

    <Spinner
      v-if="loading"
      :size="spinnerSize"
      :color="spinnerColor"
      :class="[{ 'tl-button__spinner tl-button__spinner_absolute': $slots.default || $slots.subtext }]"
    />

    <Icon v-if="iconRight" :name="iconRight" :size="iconSize" data-test-id="icon-right" class="tl-button__icon" />
  </button>
</template>

<style lang="scss">
  .tl-button {
    $root: &;

    align-items: center;
    background-color: var(--color-brand-primary);
    border: none;
    color: var(--color-text-inverse-white);
    cursor: pointer;
    display: inline-flex;
    gap: var(--spacing-xs);
    justify-content: center;
    padding: 0 var(--spacing-m);
    position: relative;
    transition: var(--transition-default);
    user-select: none;

    @include disable-tap-highlight;

    &:not(&_clickable) {
      cursor: text;
    }

    &_loading {
      cursor: default;
    }

    &__spinner {
      &_absolute {
        position: absolute;
      }
    }

    &_appearance {
      &-primary {
        &#{$root}_type-icon,
        &#{$root}_type-text,
        &#{$root}_type-link {
          #{$root}__icon {
            fill: var(--color-brand-secondary);
          }
        }

        &#{$root}_type-dash-link,
        &#{$root}_type-link,
        &#{$root}_type-text {
          color: var(--color-brand-secondary);
        }

        &#{$root}_type-dash-link {
          color: var(--color-brand-secondary);

          #{$root}__icon {
            fill: var(--color-brand-secondary);
          }
        }
      }

      &-secondary {
        &#{$root}_type-icon,
        &#{$root}_type-text,
        &#{$root}_type-link {
          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }

        &#{$root}_type-dash-link,
        &#{$root}_type-link,
        &#{$root}_type-text {
          color: var(--color-text-primary);
        }

        &#{$root}_type-dash-link {
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }

        &#{$root}_type-multiline,
        &#{$root}_type-button {
          background-color: transparent;
          border-color: var(--color-brand-primary);
          border-style: solid;
          color: var(--color-brand-primary);
        }

        &#{$root}_type-button {
          border-width: 1px;

          #{$root}__icon {
            fill: var(--color-brand-primary);
          }
        }

        &#{$root}_type-multiline {
          border-width: 2px;
        }

        &#{$root}_size-xl,
        &#{$root}_size-l {
          &#{$root}_type-button {
            border-width: 2px;
          }
        }
      }

      &-tertiary {
        &#{$root}_type-button {
          #{$root}__icon {
            fill: var(--color-text-inverse-black);
          }
        }

        &#{$root}_type-icon,
        &#{$root}_type-text,
        &#{$root}_type-link {
          #{$root}__icon {
            fill: var(--color-text-secondary);
          }
        }

        &#{$root}_type-dash-link,
        &#{$root}_type-link,
        &#{$root}_type-text {
          color: var(--color-text-secondary);
        }

        &#{$root}_type-dash-link {
          color: var(--color-text-secondary);

          #{$root}__icon {
            fill: var(--color-text-secondary);
          }
        }

        &#{$root}_type-multiline,
        &#{$root}_type-button {
          background-color: var(--color-bg-primary);
          border: 1px solid var(--color-text-primary);
          border-color: var(--color-text-primary);
          color: var(--color-text-inverse-black);
        }
      }

      &-sub-primary {
        &#{$root}_type-button {
          background-color: var(--color-brand-secondary);
          color: var(--color-text-inverse-white);

          #{$root}__icon {
            fill: var(--color-text-inverse-white);
          }
        }
      }
    }

    &_size {
      &-xl {
        &#{$root}_type-button,
        &#{$root}_type-text {
          height: 50px;
        }

        &#{$root}_type-button {
          border-radius: var(--rounding-l);
        }

        &#{$root}_type-multiline {
          min-height: 86px;
          padding: var(--spacing-s) var(--spacing-xxl);

          #{$root}__text {
            gap: var(--spacing-xxs);
          }
        }
      }

      &-l {
        &#{$root}_type-button,
        &#{$root}_type-text {
          height: 48px;
        }

        &#{$root}_type-button {
          border-radius: var(--rounding-l);
        }

        &#{$root}_type-multiline {
          min-height: 86px;
          padding: var(--spacing-s) var(--spacing-xxl);

          #{$root}__text {
            gap: var(--spacing-xxs);
          }
        }
      }

      &-m {
        &#{$root}_type-button,
        &#{$root}_type-text {
          height: 40px;
        }

        &#{$root}_type-button {
          border-radius: var(--rounding-l);
        }

        &#{$root}_type-multiline {
          min-height: 60px;
          padding: var(--spacing-xs) var(--spacing-l);

          #{$root}__text {
            gap: var(--spacing-xxxs);
          }
        }
      }

      &-s {
        &#{$root}_type-button,
        &#{$root}_type-text {
          height: 32px;
        }

        &#{$root}_type-button {
          border-radius: var(--rounding-m);
        }

        &#{$root}_type-multiline {
          gap: var(--spacing-xxs);
          min-height: 52px;
          padding: var(--spacing-xs) var(--spacing-m);
        }
      }
    }

    &_type {
      &-text,
      &-link,
      &-dash-link,
      &-icon {
        background: transparent;
      }

      &-link,
      &-icon,
      &-dash-link {
        height: auto;
        padding: 0;
      }

      &-dash-link {
        #{$root}__main-text {
          border-bottom: 1px dashed;
        }
      }

      &-text {
        border-radius: var(--rounding-s);
      }

      &-multiline {
        border-radius: var(--rounding-m);
      }
    }

    &_disabled {
      cursor: default;

      &#{$root}_appearance {
        &-tertiary,
        &-secondary,
        &-primary {
          &#{$root}_type-link,
          &#{$root}_type-dash-link,
          &#{$root}_type-text {
            color: var(--color-text-tertiary);
          }

          &#{$root}_type-multiline {
            background-color: var(--color-neutral-10);
            border: none;
            color: var(--color-text-tertiary);
          }

          &#{$root}_type-icon,
          &#{$root}_type-button,
          &#{$root}_type-link,
          &#{$root}_type-dash-link,
          &#{$root}_type-text {
            #{$root}__icon {
              fill: var(--color-text-tertiary);
            }
          }
        }

        &-tertiary,
        &-sub-primary,
        &-primary {
          &#{$root}_type-button {
            background-color: var(--color-neutral-10);
            color: var(--color-text-tertiary);
          }
        }

        &-secondary {
          &#{$root}_type-button {
            border-color: var(--color-neutral-10);
            border-width: 1px;
            color: var(--color-text-tertiary);
          }

          &#{$root}_type-multiline {
            border: none;
            padding: var(--spacing-xxs) var(--spacing-m);
          }
        }

        &-tertiary {
          &#{$root}_type-button {
            border: none;
            color: var(--color-text-tertiary);
          }
        }

        &-sub-primary {
          &#{$root}_type-button {
            #{$root}__icon {
              fill: var(--color-text-tertiary);
            }
          }
        }
      }
    }

    &__text {
      align-items: center;
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: center;

      &_hidden {
        visibility: hidden;
      }
    }

    &__icon {
      fill: var(--color-text-inverse-white);
      transition: var(--transition-default);
    }
  }
</style>
